import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function GuideDetail() {
  const { id } = useParams();
  const [g, setG] = useState(null);
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem(`guide-${id}`) || "[]"));

  useEffect(() => {
    api.get(`/guides/${id}`).then((r) => setG(r.data));
  }, [id]);

  const toggle = (i) => {
    const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
    setDone(next);
    localStorage.setItem(`guide-${id}`, JSON.stringify(next));
  };

  if (!g) return <div className="p-10 text-center font-mono text-xs text-[var(--text-muted)]">LOADING...</div>;

  const progress = Math.round((done.length / g.steps.length) * 100);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Link to="/guides" className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white inline-flex items-center gap-2" data-testid="guide-back">
        <ArrowLeft className="w-3 h-3" /> All protocols
      </Link>

      <div className="mt-6 mb-10">
        <span className="tape-label">{g.category.toUpperCase()}</span>
        <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">{g.title}</h1>
        <p className="text-[var(--text-muted)] mt-4 text-lg">{g.summary}</p>

        <div className="mt-6 grid grid-cols-3 gap-px bg-[var(--border)] max-w-md">
          <Tile label="DIFFICULTY" value={g.difficulty} />
          <Tile label="DURATION" value={g.duration} />
          <Tile label="PROGRESS" value={`${progress}%`} accent />
        </div>
      </div>

      <div className="panel">
        <div className="px-6 py-4 border-b border-[var(--border)] flex justify-between items-center">
          <span className="tape-label">THE PROTOCOL</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            {done.length}/{g.steps.length} executed
          </span>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {g.steps.map((s, i) => {
            const isDone = done.includes(i);
            return (
              <button
                key={i}
                onClick={() => toggle(i)}
                data-testid={`step-${i}`}
                className={`w-full text-left grid grid-cols-12 gap-4 px-6 py-6 items-start hover:bg-[var(--surface)] transition-colors ${isDone ? "opacity-60" : ""}`}
              >
                <div className="col-span-1 font-display text-3xl text-[var(--primary)]">0{i + 1}</div>
                <div className="col-span-10 text-sm md:text-base text-[var(--text)] leading-relaxed">{s}</div>
                <div className="col-span-1 flex justify-end">
                  <div className={`w-6 h-6 border flex items-center justify-center ${isDone ? "bg-[var(--success)] border-[var(--success)]" : "border-[var(--border-bright)]"}`}>
                    {isDone && <CheckCircle2 className="w-4 h-4 text-black" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Tile = ({ label, value, accent }) => (
  <div className="bg-[var(--bg)] p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className={`font-mono text-sm mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);
